import * as React from "react";
// @mui
import Markdown from "src/components/markdown";
import Button from "src/consuman-components/generic/button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
// hooks
import { useBoolean } from "src/hooks/use-boolean";
// locales
import { useLocales } from "src/locales";

const AlertDialog = React.forwardRef(({ ...other }, ref) => {
  const { t } = useLocales();

  const open = useBoolean(false);

  const [message, setMessage] = React.useState(null);
  const [list, setList] = React.useState(null);
  const [child, setChild] = React.useState(null);
  const [title, setTitle] = React.useState(null);
  const [agreeOnClick, setAgreeOnClick] = React.useState(null);
  const [cancelOnClick, setCancelOnClick] = React.useState(null);
  const [cancelButtonText, setCancelButtonText] = React.useState(null);
  const [agreeButtonText, setAgreeButtonText] = React.useState(null);
  const [agreeButtonColor, setAgreeButtonColor] = React.useState(null);
  const [cancelButtonColor, setCancelButtonColor] = React.useState(null);
  const [maxWidth, setMaxWidth] = React.useState(null);
  const [fullWidth, setFullWidth] = React.useState(null);
  const [scroll, setScroll] = React.useState(null);
  const [confirmationText, setConfirmationText] = React.useState(null);
  const [confirmationTextLabel, setConfirmationTextLabel] = React.useState(null);
  const [confirmationTextHelper, setConfirmationTextHelper] = React.useState(null);
  const [confirmationTextErrorEmpty, setConfirmationTextErrorEmpty] = React.useState(null);
  const [confirmationTextErrorMismatch, setConfirmationTextErrorMismatch] = React.useState(null);
  const [confirmationInputValue, setConfirmationInputValue] = React.useState("");
  const [confirmationTouched, setConfirmationTouched] = React.useState(false);
  const deleteEnabled = useBoolean(false);

  const openDialog = m => {
    setMessage(m.message);
    setList(m.list);
    setChild(m.child);
    setTitle(m.title);
    setCancelButtonText(m.cancelButtonText);
    setAgreeButtonText(m.agreeButtonText);
    setCancelButtonColor(m.cancelButtonColor || "error");
    setAgreeButtonColor(m.agreeButtonColor);
    setAgreeOnClick(agree => m.agreeOnClick);
    setCancelOnClick(cancel => m.cancelOnClick);
    setMaxWidth(m.maxWidth || "md");
    setFullWidth(m.fullWidth || false);
    setScroll(m.scroll || "paper");
    setConfirmationText(m.confirmationText);
    setConfirmationTextLabel(m.confirmationTextLabel || null);
    setConfirmationTextHelper(m.confirmationTextHelper || null);
    setConfirmationTextErrorEmpty(m.confirmationTextErrorEmpty || null);
    setConfirmationTextErrorMismatch(m.confirmationTextErrorMismatch || null);
    setConfirmationInputValue("");
    setConfirmationTouched(false);
    deleteEnabled.onFalse();

    open.onTrue();
  };

  const closeDialog = () => {
    open.onFalse();
  };

  React.useImperativeHandle(ref, () => ({
    openDialog,
    closeDialog,
  }));

  const agree = async () => {
    if (agreeOnClick) {
      await agreeOnClick();
    }

    open.onFalse();
  };

  const cancel = () => {
    closeDialog();
    if (cancelOnClick) {
      cancelOnClick();
    }
  };

  return (
    <Dialog
      {...other}
      open={open.value}
      onClose={closeDialog}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="title-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Markdown children={message} />
        </DialogContentText>
        {list ? (
          <List>
            {list.map(obj => (
              <ListItem
                key={obj.value || obj}
                dense
                component={obj.key ? "a" : null}
                target={obj.key ? "_blank" : null}
                href={obj.key ? obj.key : null}
              >
                <ListItemText primary={obj.value || obj} />
              </ListItem>
            ))}
          </List>
        ) : null}
        <>
          {child}
          {confirmationText && (
            <TextField
              label={confirmationTextLabel || t("name")}
              helperText={
                confirmationTouched && confirmationInputValue === ""
                  ? confirmationTextErrorEmpty || confirmationText
                  : confirmationTouched && confirmationInputValue !== confirmationText
                  ? confirmationTextErrorMismatch || confirmationText
                  : confirmationTextHelper || confirmationText
              }
              error={
                confirmationTouched &&
                (confirmationInputValue === "" || confirmationInputValue !== confirmationText)
              }
              fullWidth
              sx={{ mt: 1 }}
              value={confirmationInputValue}
              onChange={e => {
                setConfirmationInputValue(e.target.value);
                setConfirmationTouched(true);
                if (e.target.value === confirmationText) deleteEnabled.onTrue();
                else deleteEnabled.onFalse();
              }}
            />
          )}
        </>
      </DialogContent>
      <DialogActions>
        {cancelButtonText ? (
          <Button
            onClick={cancel}
            color={cancelButtonColor}
            text={cancelButtonText}
          />
        ) : null}
        <Button
          onClick={agree}
          color={agreeButtonColor}
          text={agreeButtonText || t("agree")}
          autoFocus
          disabled={confirmationText && !deleteEnabled.value}
        />
      </DialogActions>
    </Dialog>
  );
});

export default AlertDialog;
